import { colorSystem, type CustomIconProps, Dot, DotProps } from '@luminovo/design-system';
import React from 'react';

import { SolutionStatus } from '../../types';
import {
    SolutionStatusErrorIcon,
    SolutionStatusErrorToSuccessIcon,
    SolutionStatusErrorToWarningIcon,
    SolutionStatusSuccessIcon,
    SolutionStatusSuccessToErrorIcon,
    SolutionStatusSuccessToWarningIcon,
    SolutionStatusWariningToErrorIcon,
    SolutionStatusWariningToSuccessIcon,
    SolutionStatusWarningIcon,
} from '../icons';

/** No `color`: a pill draws its statuses' own colors. */
type PillProps = Omit<CustomIconProps, 'color'>;

export const SolutionStatusIcon: React.FunctionComponent<{
    status: SolutionStatus;
    iconProps?: PillProps;
}> = ({ status, iconProps }) => {
    switch (status) {
        case SolutionStatus.Good: {
            return <SolutionStatusSuccessIcon {...iconProps} />;
        }
        case SolutionStatus.Warning: {
            return <SolutionStatusWarningIcon {...iconProps} />;
        }
        case SolutionStatus.Error: {
            return <SolutionStatusErrorIcon {...iconProps} />;
        }
    }
};

/** 12px unless `size`, or a slot that sizes icons such as a button's start icon, sets it. */
export const SolutionStatusCircleIcon: React.FunctionComponent<
    {
        status: SolutionStatus;
    } & Omit<DotProps, 'color'>
> = ({ status, ...dotProps }) => {
    switch (status) {
        case SolutionStatus.Good: {
            return <Dot color={colorSystem.green[5]} size={12} {...dotProps} />;
        }
        case SolutionStatus.Warning: {
            return <Dot color={colorSystem.yellow[5]} size={12} {...dotProps} />;
        }
        case SolutionStatus.Error: {
            return <Dot color={colorSystem.red[5]} size={12} {...dotProps} />;
        }
    }
};

export const SolutionStatusTransitionIcon: React.FunctionComponent<{
    originalStatus: SolutionStatus;
    manualStatus: SolutionStatus;
    iconProps?: PillProps;
}> = ({ originalStatus, manualStatus, iconProps }) => {
    if (originalStatus === SolutionStatus.Good) {
        if (manualStatus === SolutionStatus.Good) {
            return <SolutionStatusSuccessIcon {...iconProps} />;
        }
        if (manualStatus === SolutionStatus.Warning) {
            return <SolutionStatusSuccessToWarningIcon {...iconProps} />;
        }
        if (manualStatus === SolutionStatus.Error) {
            return <SolutionStatusSuccessToErrorIcon {...iconProps} />;
        }
    }

    if (originalStatus === SolutionStatus.Warning) {
        if (manualStatus === SolutionStatus.Good) {
            return <SolutionStatusWariningToSuccessIcon {...iconProps} />;
        }
        if (manualStatus === SolutionStatus.Warning) {
            return <SolutionStatusWarningIcon {...iconProps} />;
        }
        if (manualStatus === SolutionStatus.Error) {
            return <SolutionStatusWariningToErrorIcon {...iconProps} />;
        }
    }

    if (originalStatus === SolutionStatus.Error) {
        if (manualStatus === SolutionStatus.Good) {
            return <SolutionStatusErrorToSuccessIcon {...iconProps} />;
        }
        if (manualStatus === SolutionStatus.Warning) {
            return <SolutionStatusErrorToWarningIcon {...iconProps} />;
        }
        if (manualStatus === SolutionStatus.Error) {
            return <SolutionStatusErrorIcon {...iconProps} />;
        }
    }

    throw new Error(`Unknown solution status transition: ${originalStatus} -> ${manualStatus}`);
};
